import React, {
  useState,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  CSSProperties,
} from 'react';

import {
  DropdownRoot,
  DropdownTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from './Dropdown';
import { DROPDOWN_MENU_MAX_HEIGHT } from './Dropdown.css';
import type { DropdownItemDef, DropdownMenuItemType } from './types';
import { Input, InputProps } from '../Input';
import { Text } from '../Text';

export interface AutocompleteProps {
  /** 현재 선택된 값 */
  value: string | undefined;
  /** 드롭다운에 표시할 아이템 목록 */
  items: DropdownMenuItemType[];
  /** 값이 변경될 때 호출되는 콜백 */
  onChange?: (value: string, item: DropdownItemDef) => void;
  /** Input placeholder */
  placeholder?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 읽기 전용 여부 */
  readOnly?: boolean;
  /** 로딩 상태 */
  loading?: boolean;
  /** 검색어 변경 시 호출되는 콜백 */
  onSearch?: (searchTerm: string) => void;
  /** 검색 결과 없을 때 메시지 */
  emptyMessage?: string;
  /**
   * 필터링 함수. 서버 사이드 필터링을 사용할 경우 `null`로 설정하면 클라이언트 필터링이 비활성화됩니다.
   * @default defaultFilterItems (클라이언트 사이드 필터링)
   */
  filterItems?:
    | ((items: DropdownMenuItemType[], searchTerm: string) => DropdownMenuItemType[])
    | null;
  /** Input 너비 */
  inputWidth?: string;
  /** 드롭다운 메뉴 너비 */
  menuWidth?: CSSProperties['width'];
  /** 드롭다운 메뉴 오프셋 */
  menuOffset?: number;
  /** 드롭다운 트리거 */
  trigger?: 'click' | 'mousedown';
  /** 스크롤 방지 여부 */
  preventScroll?: boolean;
  /** z-index */
  zIndex?: number;
  /** 선택 표시 스타일 */
  selectable?: 'single' | 'multi' | 'none';
  /** controlled open 상태 */
  open?: boolean;
  /** open 상태 변경 콜백 */
  onOpenChange?: (open: boolean) => void;
  /** 트리거 mouse down 시 호출되는 콜백 */
  onTriggerMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Input에 전달할 추가 props */
  inputProps?: Omit<
    InputProps,
    'value' | 'onChange' | 'placeholder' | 'disabled' | 'loading' | 'width' | 'ref'
  >;
  /** 선택된 값의 label을 가져오는 함수 */
  getDisplayLabel?: (value: string, items: DropdownMenuItemType[]) => React.ReactNode;
  /** blur 시 드롭다운 닫기 여부 */
  closeOnBlur?: boolean;
  /** 텍스트 에리스핑 여부 */
  ellipsis?: boolean;
  /** 스크롤 가상화 활성화 여부 (많은 항목을 렌더링할 때 사용) */
  virtualized?: boolean;
  /** 드롭다운 메뉴 최대 높이 */
  maxHeight?: number;
}

export interface AutocompleteHandle {
  focus: () => void;
  blur: () => void;
  getInputElement: () => HTMLInputElement | null;
}

const defaultFilterItems = (
  items: DropdownMenuItemType[],
  searchTerm: string,
): DropdownMenuItemType[] => {
  if (!searchTerm) return items;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return items.filter((item) => {
    if (item.type === 'divider') return false;
    if (item.type === 'subMenu') return true;
    if (typeof item.label === 'string') {
      return item.label.toLowerCase().includes(lowerSearchTerm);
    }
    return true;
  });
};

const defaultGetDisplayLabel = (value: string, items: DropdownMenuItemType[]): React.ReactNode => {
  const item = items.find((i) => i.type === 'item' && i.key === value) as
    | DropdownItemDef
    | undefined;
  if (item && typeof item.label === 'string') {
    return item.label;
  }
  return value;
};

export const Autocomplete = forwardRef<AutocompleteHandle, AutocompleteProps>(
  (
    {
      value,
      items,
      onChange,
      placeholder,
      disabled = false,
      readOnly = false,
      loading = false,
      onSearch,
      filterItems = defaultFilterItems,
      emptyMessage = '검색 결과가 없습니다',
      inputWidth = '100%',
      menuWidth,
      menuOffset = 4,
      preventScroll = true,
      zIndex,
      selectable,
      open,
      onOpenChange,
      inputProps,
      getDisplayLabel = defaultGetDisplayLabel,
      closeOnBlur = true,
      trigger = 'click',
      onTriggerMouseDown,
      ellipsis = false,
      virtualized = false,
      maxHeight = DROPDOWN_MENU_MAX_HEIGHT,
    },
    ref,
  ) => {
    const label = getDisplayLabel(value || '', items);
    const [internalOpen, setInternalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const { onKeyDown, onBlur, ...restInputProps } = inputProps || {};

    // controlled/uncontrolled 패턴
    const isOpen = useMemo(() => open ?? internalOpen, [open, internalOpen]);
    const setIsOpen = useMemo(() => onOpenChange ?? setInternalOpen, [onOpenChange]);

    // ref를 통해 외부에서 접근 가능한 메서드 노출
    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      getInputElement: () => inputRef.current,
    }));

    // 필터링된 아이템들
    const filteredItems = useMemo(
      () => (filterItems ? filterItems(items, searchTerm) : items),
      [items, searchTerm, filterItems],
    );

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setSearchTerm('');
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = e.target.value;
      setSearchTerm(newSearchTerm);
      onSearch?.(newSearchTerm);

      // 타이핑 시 드롭다운 열기
      if (!isOpen && newSearchTerm.length > 0) {
        handleOpenChange(true);
      }
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      if (closeOnBlur) {
        // blur 시 드롭다운 닫기 (약간의 딜레이를 줘서 클릭 이벤트가 먼저 처리되도록)
        setTimeout(() => {
          handleOpenChange(false);
        }, 150);
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);

      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        handleOpenChange(false);
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
      }
    };

    const handleItemClick = (
      _e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
      item: DropdownItemDef,
    ) => {
      onChange?.(item.key, item);
      handleOpenChange(false);
    };

    return (
      <DropdownRoot
        open={isOpen}
        onOpenChange={handleOpenChange}
        width={menuWidth}
        menuOffset={menuOffset}
        itemCount={filteredItems.length}
        disableItemFocus
        trigger={trigger}
        maxHeight={maxHeight}
      >
        <DropdownTrigger disabled={disabled} readOnly={readOnly} asChild={false}>
          <InputOverlayLabel
            label={label}
            showLabel={searchTerm.length === 0 && !open}
            ellipsis={ellipsis}
            onMouseDown={onTriggerMouseDown}
          >
            <Input
              ref={inputRef}
              value={searchTerm}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              loading={loading}
              width={inputWidth}
              {...restInputProps}
            />
          </InputOverlayLabel>
        </DropdownTrigger>
        <DropdownMenu preventScroll={preventScroll} zIndex={zIndex} disableFocusTrap={true}>
          {filteredItems.length === 0 ? (
            <DropdownMenuItem disabled>{emptyMessage}</DropdownMenuItem>
          ) : (
            <DropdownMenuContent
              items={filteredItems}
              onItemClick={handleItemClick}
              selectedKeys={value ? [value] : undefined}
              selectable={selectable}
              virtualized={virtualized}
            />
          )}
        </DropdownMenu>
      </DropdownRoot>
    );
  },
);

Autocomplete.displayName = 'Autocomplete';

const InputOverlayLabel = ({
  label,
  children,
  showLabel,
  ellipsis = false,
  ...props
}: {
  label: React.ReactNode;
  showLabel: boolean;
  children: React.ReactNode;
  ellipsis?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div style={{ position: 'relative' }} {...props}>
      {children}
      {showLabel && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Text
            textStyle="13_400"
            ellipsis={ellipsis}
            showTooltipOnEllipsis={ellipsis}
            ignoreTextStyle
          >
            {label}
          </Text>
        </div>
      )}
    </div>
  );
};
